import React, { useEffect, useMemo, useState } from "react";
import { FaBolt, FaCrown, FaGamepad, FaRedoAlt, FaTrophy, FaUser } from "react-icons/fa";
import { getGameRoomOverview, resetPersonalGameSeason } from "../../../services/games/gameService";
import "./GameRoomScoreboard.css";

const GAME_LABELS = {
  "meteor-smash": "Meteor Smash",
  minesweeper: "Minesweeper",
  chess: "Chess",
  blackjack: "Blackjack",
  "block-drop": "Block Drop",
};

function formatLeaderboardValue(row) {
  const value = Number(row.value || 0);
  if (row.category === "expert_fastest") {
    return `${(value / 1000).toFixed(value < 100000 ? 1 : 0)}s`;
  }
  if (row.category === "blackjack_net") {
    const sign = value > 0 ? "+" : "";
    return `${sign}${value.toLocaleString()} chips`;
  }
  if (row.category === "chess_wins") {
    return `${value.toLocaleString()} wins`;
  }
  return value.toLocaleString();
}

function formatPersonalPrimary(row) {
  if (!row) return "—";
  const gameKey = row.game_key;

  if (gameKey === "minesweeper") {
    if (!row.lifetime_best_duration_ms) return "No win yet";
    return `${(Number(row.lifetime_best_duration_ms) / 1000).toFixed(1)}s best`;
  }

  if (gameKey === "chess") {
    return `${row.lifetime_wins || 0}-${row.lifetime_losses || 0}-${row.lifetime_draws || 0}`;
  }

  if (gameKey === "blackjack") {
    const net = Number(row.lifetime_net || 0);
    return `${net > 0 ? "+" : ""}${net.toLocaleString()} chips`;
  }

  return Number(row.lifetime_high_score || 0).toLocaleString();
}

export default function GameRoomScoreboard({ currentUser }) {
  const [leaderboards, setLeaderboards] = useState([]);
  const [personal, setPersonal] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [resetting, setResetting] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await getGameRoomOverview();
      setLeaderboards(data.leaderboards || []);
      setPersonal(data.personal || []);
    } catch (err) {
      console.error("Game Room scoreboard failed to load:", err);
      setError("Scoreboards are unavailable until the Game Room database migration is installed.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [currentUser?.id]);

  const champions = useMemo(() => {
    const seen = new Set();
    return leaderboards.filter((row) => {
      const key = `${row.game_key}:${row.category}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [leaderboards]);

  const handleReset = async (gameKey) => {
    if (!currentUser?.id || resetting) return;
    const ok = window.confirm(
      `Start a new personal ${GAME_LABELS[gameKey] || gameKey} season? Lifetime records will stay intact.`
    );
    if (!ok) return;

    setResetting(gameKey);
    try {
      await resetPersonalGameSeason(gameKey);
      await load();
    } catch (err) {
      console.error("Failed to reset game season:", err);
      setError(err.message || "Could not reset this personal season.");
    } finally {
      setResetting("");
    }
  };

  return (
    <section className="game-room-scoreboard" aria-label="Hobbli Game Room scoreboards">
      <div className="game-room-scoreboard__heading">
        <div>
          <span>PLAYER ONE · HIGH SCORE WALL</span>
          <h2>Arcade Scoreboards</h2>
        </div>
        <p>Lifetime records stay forever. Personal seasons can start fresh whenever you want.</p>
      </div>

      {error ? <div className="game-room-scoreboard__notice">{error}</div> : null}

      <div className="game-room-scoreboard__grid">
        <article className="game-room-scoreboard__panel">
          <div className="game-room-scoreboard__panel-title">
            <FaCrown />
            <div>
              <span>ALL-TIME</span>
              <h3>Arcade Leaders</h3>
            </div>
          </div>

          {loading ? (
            <p className="game-room-scoreboard__empty">Loading the high-score board…</p>
          ) : champions.length ? (
            <div className="game-room-leaders">
              {champions.slice(0, 5).map((row, index) => (
                <div className="game-room-leader-row" key={`${row.game_key}:${row.category}`}>
                  <span className="game-room-leader-rank">{String(index + 1).padStart(2, "0")}</span>
                  <div>
                    <strong>{GAME_LABELS[row.game_key] || row.game_key}</strong>
                    <span>{row.display_name || "Player"}</span>
                  </div>
                  <b>{formatLeaderboardValue(row)}</b>
                </div>
              ))}
            </div>
          ) : (
            <p className="game-room-scoreboard__empty">No scores yet. The first cabinet record is waiting.</p>
          )}
        </article>

        <article className="game-room-scoreboard__panel game-room-scoreboard__panel--personal">
          <div className="game-room-scoreboard__panel-title">
            <FaUser />
            <div>
              <span>PERSONAL SCOREBOARD</span>
              <h3>{currentUser?.display_name || "Guest Player"}</h3>
            </div>
          </div>

          {!currentUser ? (
            <div className="game-room-scoreboard__guest">
              <FaGamepad />
              <p>Solo games stay playable as a guest. Sign in to save Hobbli records, seasons, and online results.</p>
            </div>
          ) : loading ? (
            <p className="game-room-scoreboard__empty">Loading your player card…</p>
          ) : personal.filter((row) => Number(row.lifetime_games || 0) > 0).length ? (
            <div className="game-room-personal-list">
              {personal
                .filter((row) => Number(row.lifetime_games || 0) > 0)
                .map((row) => (
                <div className="game-room-personal-row" key={row.game_key}>
                  <div>
                    <strong>{GAME_LABELS[row.game_key] || row.game_key}</strong>
                    <span>
                      {row.lifetime_games || 0} lifetime · {row.season_games || 0} this season
                    </span>
                  </div>
                  <b>{formatPersonalPrimary(row)}</b>
                  <button
                    type="button"
                    onClick={() => handleReset(row.game_key)}
                    disabled={resetting === row.game_key}
                    title="Start a fresh personal season"
                  >
                    <FaRedoAlt />
                    {resetting === row.game_key ? "Resetting" : "New Season"}
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p className="game-room-scoreboard__empty">Play a game to start your personal scoreboard.</p>
          )}
        </article>
      </div>

      <div className="game-room-scoreboard__ticker" aria-hidden="true">
        <span><FaTrophy /> HIGH SCORES</span>
        <span><FaBolt /> FASTEST TIMES</span>
        <span><FaGamepad /> BIGGEST WINNERS</span>
      </div>
    </section>
  );
}
